import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../environments/environment';
import { ErrorService } from './error.service';
import { MsAuthService } from './ms-auth.service';
import { IOrder, IOrderItem } from "./sale.service";
import { TranslateService } from "@ngx-translate/core";

export interface IDataItem{
  type: string;
  id: string;
  status: string;
}

export interface IMessageItem{
  messageDetailId: string;
  messageDetailValue: string;
}

export interface IStatusItem{
  type: string;
  id: string;
  classId: string;
  number: string;
  message: string;
  messageDetails: IMessageItem[];
}

export interface IOrderHoldCancelResponse {
  data?: IDataItem[],
  statuses?: IStatusItem[],
}

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private _host = environment.apigee.host;
  
  // skipcq
  constructor(
    private _httpClient: HttpClient,
    private _errorService: ErrorService,
    private _msAuthService: MsAuthService,
    private _translate: TranslateService,
  ) {}

  public reassignStore(orderId: string, lineItem: string, locationId: string): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const url = `${this._host}/orders`;
    const uuid = uuidv4()
    const body = {
      data: [
        {
          type: "orders",
          id: uuid,
          attributes: {
            salesChannelOrderId: orderId,
            holdUser: this._msAuthService.getName(),
            orderProducts: [
              {
                lineItem: lineItem,
                fulfillmentStatus: [
                  {
                      locationId: locationId
                  }
                ]
              }
            ]
          }
        }
      ]
    }
    return this._httpClient.patch(url, JSON.stringify(body), {headers: headers}).pipe(
      catchError((e) => {
        console.log(e);
        this._errorService.showGeneralError(true);
        throw e;
      })
    );
  }

  public holdEntireOrder(orderId: string, userId: string, orders: IOrder,reasonCode:string): Observable<IDataItem> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const url = `${this._host}/orders`;
    const uuid = uuidv4();
    const orderProducts = orders.items.map((item: IOrderItem) => (
      {
        lineItem: item.lineItem,
        quantity: item.quantity,
        productId: item.productId,
        fulfillmentStatus: [
          {
            statusCode: "17",
            timestamp: new Date().toISOString()
          }
        ]
      }
    ));
    
    const body = {
      data: [
        {
          id: uuid,
          type: "orders",
          attributes: {
            salesChannelOrderId: orderId,
            deliveryBlock: "ZC",
            userId: userId,
            timestamp: new Date().toISOString(),
            orderProducts: orderProducts
          }
        }
      ]
    }
    
    return this._httpClient.patch(url, JSON.stringify(body), {headers: headers}).pipe(
      mergeMap((result:IOrderHoldCancelResponse) => {
        if(result && result.data && result.data[0].status === 'ok'){
          return this.cancelEntireOrder(orderId,userId,orders,reasonCode);
        } else {
          throw new Error(`Failed to Hold Order number: ${orderId}`);
        }
      }),
    );
  }

  public cancelEntireOrder(orderId: string, userId: string, orders: IOrder,reasonCode: string): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const url = `${this._host}/orders`;
    const orderProducts = orders.items.map((item: IOrderItem) => (
      {
        lineItem: item.lineItem,
        quantity: item.quantity,
        productId: item.productId,
        fulfillmentStatus: [
          {
            statusCode: "5",
            reasonCode: reasonCode,
            timestamp: new Date().toISOString()
          }
        ]
      }
    ));
    
    const body = {
      data: [
        {
          id: orderId,
          type: "orders",
          attributes: {
            salesChannelOrderId: orderId,
            deliveryBlock: "ZC",
            userId: userId,
            timestamp: new Date().toISOString(),
            orderProducts: orderProducts
          }
        }
      ]
    }

    return this._httpClient.patch(url, JSON.stringify(body), {headers: headers})
      .pipe(map((result:IOrderHoldCancelResponse) => {
        if(result && result.data && result.data[0].status === 'ok'){
          return result.data[0]
        }else {
          throw new Error(`Failed to Cancel Order number ${orderId}`);
        }
      })
    );
  }
  
  public getSource(source: string): string {
    const appCode = [76,77];
    const webCode = [91,92,93,74];
    const retailCode = [84,85];
    const conciergeCode = [75,88,89,96,97]
    
    if(appCode.includes(parseInt(source))){
      return `${this._translate.instant('siteSource.app')}`;
    } else if(webCode.includes(parseInt(source))) {
      return `${this._translate.instant('siteSource.web')}`;
    }else if(retailCode.includes(parseInt(source))) {
      return '';
    }else if(conciergeCode.includes(parseInt(source))) {
      return `${this._translate.instant('siteSource.concierge')}`;
    } else {
      return `${this._translate.instant('siteSource.other')}`;
    }
  }
}
